/**
 * WhatsApp Admin Powerhouse — Control Center.
 * Plan: .claude/plans/whatsapp-admin-powerhouse.md (revision 2)
 *
 * Tabs:
 *   1. Connections — merchant WABA + phone + catalog state (this commit)
 *   2. Usage       — per-merchant rollup + platform totals (rollout step 4)
 *   3. Limits      — tier caps + per-merchant overrides    (step 5)
 *   4. Defaults    — automation defaults + kill switch     (step 6)
 *
 * CSS is scoped with `wapc-*` per CLAUDE.md so nothing collides with
 * the global theme.css.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Search,
  RefreshCw,
  X,
  Eye,
  Pause,
  Play,
  PlugZap,
  StickyNote,
  AlertTriangle,
  Info,
} from 'lucide-react';
import './WhatsAppControlCenter.css';

const api = (path, opts = {}) => {
  const base = process.env.REACT_APP_BACKEND_URL || '/api';
  return fetch(base + path, {
    credentials: 'include',
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
  });
};

const TABS = [
  { key: 'connections', label: 'Connections' },
  { key: 'usage',       label: 'Usage' },
  { key: 'limits',      label: 'Limits' },
  { key: 'defaults',    label: 'Defaults' },
  { key: 'broadcast',   label: 'Broadcasts' },
];

const STATUS_META = {
  ACTIVE:       { color: '#0d7463', label: 'Active' },
  PENDING:      { color: '#d97706', label: 'Pending' },
  SUSPENDED:    { color: '#dc2626', label: 'Suspended' },
  DISCONNECTED: { color: '#6b7280', label: 'Disconnected' },
  EXPIRED:      { color: '#dc2626', label: 'Expired' },
  READY:        { color: '#0d7463', label: 'Ready' },
  QUEUED:       { color: '#7c3aed', label: 'Queued' },
  SENDING:      { color: '#2563eb', label: 'Sending' },
  SENT:         { color: '#0284c7', label: 'Sent' },
  DELIVERED:    { color: '#0d9488', label: 'Delivered' },
  READ:         { color: '#16a34a', label: 'Read' },
  COMPLETED:    { color: '#0d7463', label: 'Completed' },
  PARTIAL:      { color: '#d97706', label: 'Partial' },
  FAILED:       { color: '#dc2626', label: 'Failed' },
  DEAD_LETTER:  { color: '#991b1b', label: 'Dead letter' },
  SKIPPED:      { color: '#6b7280', label: 'Skipped' },
  CANCELLED:    { color: '#6b7280', label: 'Cancelled' },
  PAUSED:       { color: '#9333ea', label: 'Paused' },
};

const BROADCAST_TYPES = [
  { value: 'STORE_LAUNCH', label: 'Store launch' },
  { value: 'NEW_PRODUCT', label: 'New product' },
  { value: 'DISCOUNT_CODE', label: 'Discount code' },
  { value: 'PROMO', label: 'Promo' },
];

function relTime(ts) {
  if (!ts) return '—';
  const t = new Date(ts).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - t) / 1000));
  if (s < 60)       return `${s}s ago`;
  if (s < 3600)     return `${Math.floor(s / 60)}m ago`;
  if (s < 86400)    return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function money(value, currency = 'NGN') {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function StatusDot({ status }) {
  const meta = STATUS_META[status] || { color: '#6b7280', label: status };
  return (
    <span className="wapc-status">
      <span className="wapc-status__dot" style={{ background: meta.color }} />
      <span>{meta.label}</span>
    </span>
  );
}

function TierBadge({ tier }) {
  if (!tier) return <span className="wapc-tier wapc-tier--free">—</span>;
  const cls = `wapc-tier wapc-tier--${String(tier).toLowerCase()}`;
  return <span className={cls}>{tier}</span>;
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  Connections tab                                                        */
/* ══════════════════════════════════════════════════════════════════════ */

function ConnectionsTab() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [search, setSearch]   = useState('');
  const [status, setStatus]   = useState('');
  const [provider, setProvider] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [detail, setDetail]   = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('page', String(pagination.page));
      params.set('limit', String(pagination.limit));
      if (search.trim()) params.set('search', search.trim());
      if (status)   params.set('status', status);
      // Note: provider filter isn't in the backend query yet — filter
      // client-side for now, add server-side in step 3.
      const res  = await api(`/admin/whatsapp/connections?${params.toString()}`);
      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(json?.error || `HTTP ${res.status}`);
      }
      const filtered = provider
        ? (json.data || []).filter((c) => String(c.provider).toUpperCase() === provider)
        : (json.data || []);
      setItems(filtered);
      setPagination((p) => ({ ...p, total: json.pagination?.total || filtered.length, totalPages: json.pagination?.totalPages || 1 }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, status, provider]);

  useEffect(() => { load(); }, [load]);

  const openDetail = useCallback(async (id) => {
    setDetailLoading(true);
    setDetail({ loading: true });
    try {
      const res  = await api(`/admin/whatsapp/connections/${id}`);
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setDetail(json.data || json);
    } catch (err) {
      setDetail({ error: err.message });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  return (
    <div className="wapc-tab-body">
      {/* Filters row */}
      <div className="wapc-filters">
        <div className="wapc-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search by merchant, phone, WABA id…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
          />
        </div>
        <select className="wapc-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="PENDING">Pending</option>
          <option value="SUSPENDED">Suspended</option>
          <option value="DISCONNECTED">Disconnected</option>
        </select>
        <select className="wapc-select" value={provider} onChange={(e) => setProvider(e.target.value)}>
          <option value="">All providers</option>
          <option value="META">Meta</option>
          <option value="TWILIO">Twilio (legacy)</option>
        </select>
        <button type="button" className="wapc-btn" onClick={load} disabled={loading}>
          <RefreshCw size={14} /> {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="wapc-error"><Info size={14} /> {error}</div>}

      {/* Connections table */}
      <div className="wapc-table-wrap">
        <table className="wapc-table">
          <thead>
            <tr>
              <th>Merchant</th>
              <th>Phone</th>
              <th>Tier</th>
              <th>Status</th>
              <th>Catalog</th>
              <th>Conversations</th>
              <th>Messages</th>
              <th>Last webhook</th>
              <th>Last sent</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr><td colSpan={10} className="wapc-empty">No connections match these filters.</td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id}>
                <td>
                  <div className="wapc-merchant">
                    <strong>{c.merchant?.storeName || c.displayName || '—'}</strong>
                    <span className="wapc-merchant__meta">
                      {c.merchant?.storeSlug ? `/${c.merchant.storeSlug}` : ''}
                      {c.merchant?.email ? ` • ${c.merchant.email}` : ''}
                    </span>
                  </div>
                </td>
                <td className="wapc-mono">{c.phoneNumber || '—'}</td>
                <td><TierBadge tier={c.merchant?.tier} /></td>
                <td><StatusDot status={c.status} /></td>
                <td>
                  {c.catalogId
                    ? <span className="wapc-catalog wapc-catalog--ready" title={`catalogId ${c.catalogId}`}>
                        {c.catalogStatus || 'READY'} ({c.catalogProductCount ?? 0})
                      </span>
                    : <span className="wapc-catalog wapc-catalog--none">Not provisioned</span>}
                </td>
                <td className="wapc-num">{c.stats?.conversations ?? 0}</td>
                <td className="wapc-num">{c.stats?.messages ?? 0}</td>
                <td>{relTime(c.lastWebhookReceived)}</td>
                <td>{relTime(c.lastMessageSent)}</td>
                <td>
                  <button
                    type="button"
                    className="wapc-btn wapc-btn--ghost"
                    onClick={() => openDetail(c.id)}
                    aria-label="View detail"
                  >
                    <Eye size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {detail && (
        <div className="wapc-drawer-scrim" onClick={() => setDetail(null)}>
          <aside className="wapc-drawer" onClick={(e) => e.stopPropagation()}>
            <header className="wapc-drawer__head">
              <h3>Connection detail</h3>
              <button type="button" className="wapc-btn wapc-btn--ghost" onClick={() => setDetail(null)}>
                <X size={16} />
              </button>
            </header>
            <div className="wapc-drawer__body">
              {detailLoading && <div className="wapc-detail-loading">Loading…</div>}
              {detail.error && <div className="wapc-error"><Info size={14} /> {detail.error}</div>}
              {!detailLoading && !detail.error && detail.id && (
                <ConnectionDetail
                  data={detail}
                  onMutated={(updated) => {
                    // Merge the updated connection into the list + drawer
                    // so the UI reflects the change without a full reload.
                    setDetail((cur) => ({ ...cur, ...updated }));
                    setItems((cur) => cur.map((c) => c.id === updated.id ? { ...c, ...updated } : c));
                  }}
                />
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

/**
 * Small confirm dialog. Used for pause / resume / disconnect. The
 * `requireReason` flag turns on a required text field — disconnect uses
 * it (backend enforces a min 3-char reason).
 */
function ConfirmDialog({ title, message, confirmLabel, danger, requireReason, busy, onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  const disabled = busy || (requireReason && reason.trim().length < 3);
  return (
    <div className="wapc-modal-scrim" onClick={onCancel}>
      <div className="wapc-modal" onClick={(e) => e.stopPropagation()}>
        <h4>{title}</h4>
        <p className="wapc-modal__msg">{message}</p>
        {requireReason ? (
          <label className="wapc-modal__reason">
            <span>Reason (required)</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Compliance issue, disputed billing, spam report, etc."
            />
          </label>
        ) : (
          <label className="wapc-modal__reason">
            <span>Reason (optional)</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Optional context for the audit log."
            />
          </label>
        )}
        <div className="wapc-modal__actions">
          <button type="button" className="wapc-btn wapc-btn--ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button"
            className={`wapc-btn ${danger ? 'wapc-btn--danger' : 'wapc-btn--primary'}`}
            onClick={() => onConfirm(reason.trim() || null)}
            disabled={disabled}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConnectionDetail({ data, onMutated }) {
  const [confirm, setConfirm] = useState(null); // { kind, danger, requireReason, title, message }
  const [note, setNote]       = useState('');
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState('');

  const isSuspended    = data.status === 'SUSPENDED';
  const isDisconnected = data.status === 'DISCONNECTED';

  const runAction = useCallback(async (path, body) => {
    setBusy(true);
    setError('');
    try {
      const res = await api(`/admin/whatsapp/connections/${data.id}${path}`, {
        method: 'POST',
        body: JSON.stringify(body || {}),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      onMutated?.(json.data);
      setConfirm(null);
      setNote('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, [data.id, onMutated]);

  const submitNote = useCallback(async () => {
    if (!note.trim()) return;
    await runAction('/notes', { note: note.trim() });
  }, [note, runAction]);

  return (
    <div className="wapc-detail">
      {/* Action row */}
      <div className="wapc-actions">
        {!isSuspended && !isDisconnected && (
          <button
            type="button"
            className="wapc-btn"
            onClick={() => setConfirm({
              kind: 'pause',
              title: 'Pause this connection?',
              message: 'The WABA stays connected — inbound Meta webhooks continue to arrive — but the bot layer and outbound sends go dark until you resume.',
              confirmLabel: 'Pause connection',
            })}
          >
            <Pause size={14} /> Pause
          </button>
        )}
        {isSuspended && (
          <button
            type="button"
            className="wapc-btn wapc-btn--primary"
            onClick={() => setConfirm({
              kind: 'resume',
              title: 'Resume this connection?',
              message: 'Bot layer + outbound sends will start working again immediately.',
              confirmLabel: 'Resume',
            })}
          >
            <Play size={14} /> Resume
          </button>
        )}
        {!isDisconnected && (
          <button
            type="button"
            className="wapc-btn wapc-btn--danger-ghost"
            onClick={() => setConfirm({
              kind: 'disconnect',
              danger: true,
              requireReason: true,
              title: 'Disconnect this WABA?',
              message: 'Clears the access token. Merchant must run Embedded Signup again to reconnect. Not reversible from the admin side.',
              confirmLabel: 'Disconnect',
            })}
          >
            <PlugZap size={14} /> Disconnect
          </button>
        )}
      </div>

      {error && <div className="wapc-error"><AlertTriangle size={14} /> {error}</div>}

      {/* Fields */}
      <div className="wapc-kv"><span>Merchant</span><strong>{data.merchant?.storeName || data.displayName || '—'}</strong></div>
      <div className="wapc-kv"><span>Merchant email</span><strong>{data.merchant?.email || '—'}</strong></div>
      <div className="wapc-kv"><span>Tier</span><strong><TierBadge tier={data.merchant?.tier} /></strong></div>
      <div className="wapc-kv"><span>Phone</span><strong className="wapc-mono">{data.phoneNumber || '—'}</strong></div>
      <div className="wapc-kv"><span>Provider</span><strong>{data.provider || '—'}</strong></div>
      <div className="wapc-kv"><span>Management</span><strong>{data.managementType || '—'}</strong></div>
      <div className="wapc-kv"><span>Status</span><strong><StatusDot status={data.status} /></strong></div>
      <div className="wapc-kv"><span>Phone number id</span><strong className="wapc-mono">{data.phoneNumberId || '—'}</strong></div>
      <div className="wapc-kv"><span>WABA id</span><strong className="wapc-mono">{data.wabaId || data.businessAccountId || '—'}</strong></div>
      <div className="wapc-kv"><span>Catalog id</span><strong className="wapc-mono">{data.catalogId || '—'}</strong></div>
      <div className="wapc-kv"><span>Catalog status</span><strong>{data.catalogStatus || '—'}</strong></div>
      <div className="wapc-kv"><span>Catalog products</span><strong>{data.catalogProductCount ?? '—'}</strong></div>
      <div className="wapc-kv"><span>Last webhook</span><strong>{relTime(data.lastWebhookReceived)}</strong></div>
      <div className="wapc-kv"><span>Last sent</span><strong>{relTime(data.lastMessageSent)}</strong></div>
      <div className="wapc-kv"><span>Created</span><strong>{relTime(data.createdAt)}</strong></div>

      {/* Add note */}
      <div className="wapc-detail-block">
        <h4><StickyNote size={12} /> Add admin note</h4>
        <textarea
          className="wapc-note-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="Merchant confirmed compliance issue over phone…"
        />
        <div className="wapc-actions">
          <button
            type="button"
            className="wapc-btn wapc-btn--primary"
            onClick={submitNote}
            disabled={busy || !note.trim()}
          >
            Add note
          </button>
        </div>
      </div>

      {data.adminNotes && (
        <div className="wapc-detail-block">
          <h4>Notes history</h4>
          <p className="wapc-notes">{data.adminNotes}</p>
        </div>
      )}

      {data.settings && Object.keys(data.settings).length > 0 && (
        <div className="wapc-detail-block">
          <h4>Automation settings</h4>
          <pre className="wapc-json">{JSON.stringify(data.settings, null, 2)}</pre>
        </div>
      )}

      {/* Confirm dialog for pause/resume/disconnect */}
      {confirm && (
        <ConfirmDialog
          {...confirm}
          busy={busy}
          onCancel={() => setConfirm(null)}
          onConfirm={(reason) => {
            const map = { pause: '/pause', resume: '/resume', disconnect: '/disconnect' };
            runAction(map[confirm.kind], reason ? { reason } : {});
          }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  Usage tab                                                              */
/* ══════════════════════════════════════════════════════════════════════ */

function pctColor(pct) {
  if (pct == null)  return '#9ca3af';
  if (pct >= 100)   return '#dc2626';
  if (pct >= 80)    return '#d97706';
  if (pct >= 50)    return '#0d7463';
  return '#6b7280';
}

function fmt(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-NG');
}

function UsageTab() {
  const [rows, setRows]       = useState([]);
  const [totals, setTotals]   = useState(null);
  const [yearMonth, setYearMonth] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [rebuilding, setRebuilding] = useState(false);

  const load = useCallback(async (ym) => {
    setLoading(true);
    setError('');
    try {
      const qs = ym ? `?yearMonth=${encodeURIComponent(ym)}` : '';
      const res  = await api(`/admin/whatsapp/usage${qs}`);
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setRows(json.data || []);
      setTotals(json.totals || null);
      setYearMonth(json.yearMonth);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rebuildNow = useCallback(async () => {
    setRebuilding(true);
    setError('');
    try {
      const res = await api('/admin/whatsapp/usage/rebuild', {
        method: 'POST',
        body: JSON.stringify({ yearMonth }),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      await load(yearMonth);
    } catch (err) {
      setError(err.message);
    } finally {
      setRebuilding(false);
    }
  }, [yearMonth, load]);

  // Month picker options — current and previous 5 months in Lagos.
  const monthOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      opts.push(`${y}-${m}`);
    }
    return opts;
  }, []);

  return (
    <div className="wapc-tab-body">
      {/* Summary cards */}
      <div className="wapc-usage-summary">
        <div className="wapc-usage-card">
          <div className="wapc-usage-card__label">Merchants with activity</div>
          <div className="wapc-usage-card__value">{fmt(totals?.merchantCount)}</div>
        </div>
        <div className="wapc-usage-card">
          <div className="wapc-usage-card__label">Messages sent</div>
          <div className="wapc-usage-card__value">{fmt(totals?.messagesSent)}</div>
        </div>
        <div className="wapc-usage-card">
          <div className="wapc-usage-card__label">Messages received</div>
          <div className="wapc-usage-card__value">{fmt(totals?.messagesReceived)}</div>
        </div>
        <div className="wapc-usage-card">
          <div className="wapc-usage-card__label">Active conversations</div>
          <div className="wapc-usage-card__value">{fmt(totals?.activeConversations)}</div>
        </div>
      </div>

      {/* Filter row */}
      <div className="wapc-filters">
        <select
          className="wapc-select"
          value={yearMonth}
          onChange={(e) => { setYearMonth(e.target.value); load(e.target.value); }}
        >
          {monthOptions.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <button type="button" className="wapc-btn" onClick={() => load(yearMonth)} disabled={loading}>
          <RefreshCw size={14} /> {loading ? 'Loading…' : 'Refresh'}
        </button>
        <button type="button" className="wapc-btn" onClick={rebuildNow} disabled={rebuilding || loading}>
          {rebuilding ? 'Rebuilding…' : 'Force rebuild'}
        </button>
        <span className="wapc-hint">
          <Info size={14} /> Rollup rebuilds nightly at 02:15 Lagos. Use Force rebuild for live numbers.
        </span>
      </div>

      {error && <div className="wapc-error"><AlertTriangle size={14} /> {error}</div>}

      {/* Table */}
      <div className="wapc-table-wrap">
        <table className="wapc-table">
          <thead>
            <tr>
              <th>Merchant</th>
              <th>Tier</th>
              <th>Messages sent</th>
              <th>Received</th>
              <th>Active convs</th>
              <th>% of msg cap</th>
              <th>Cap</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={7} className="wapc-empty">No usage recorded for {yearMonth}.</td></tr>
            )}
            {rows.map((r) => {
              const pct = r.pctUsed?.messages;
              const cap = r.limits?.messages;
              const capLabel = cap === -1 ? '∞' : fmt(cap);
              return (
                <tr key={r.merchantId}>
                  <td>
                    <div className="wapc-merchant">
                      <strong>{r.merchant?.storeName || r.merchant?.email || '—'}</strong>
                      <span className="wapc-merchant__meta">
                        {r.merchant?.storeSlug ? `/${r.merchant.storeSlug}` : ''}
                        {r.merchant?.email ? ` • ${r.merchant.email}` : ''}
                      </span>
                    </div>
                  </td>
                  <td><TierBadge tier={r.merchant?.tier} /></td>
                  <td className="wapc-num">{fmt(r.messagesSent)}</td>
                  <td className="wapc-num">{fmt(r.messagesReceived)}</td>
                  <td className="wapc-num">{fmt(r.activeConversations)}</td>
                  <td>
                    <div className="wapc-pct">
                      <div className="wapc-pct__bar">
                        <span
                          className="wapc-pct__fill"
                          style={{ width: pct != null ? `${Math.min(100, pct)}%` : '0%', background: pctColor(pct) }}
                        />
                      </div>
                      <span className="wapc-pct__label" style={{ color: pctColor(pct) }}>
                        {pct == null ? '—' : `${pct}%`}
                      </span>
                    </div>
                  </td>
                  <td className="wapc-num">
                    {capLabel}
                    {r.limits?.overridden?.message && (
                      <span className="wapc-mini-pill" title="Merchant has an active override">override</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  Limits tab — tier caps + per-merchant overrides                        */
/* ══════════════════════════════════════════════════════════════════════ */

const capLabel = (n) => (n === -1 ? '∞' : (n == null ? '—' : Number(n).toLocaleString('en-NG')));

function LimitsTab() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingTier, setSavingTier] = useState(null);
  const [tierDraft, setTierDraft] = useState({});
  const [overrideForm, setOverrideForm] = useState(null); // { merchant, override, effective }
  const [merchantSearch, setMerchantSearch] = useState('');
  const [searchState, setSearchState] = useState({ loading: false, error: '' });

  const loadTiers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api('/admin/whatsapp/limits/tiers');
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setTiers(json.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadTiers(); }, [loadTiers]);

  const saveTier = useCallback(async (tier) => {
    const draft = tierDraft[tier] || {};
    setSavingTier(tier);
    setError('');
    try {
      const res = await api(`/admin/whatsapp/limits/tiers/${encodeURIComponent(tier)}`, {
        method: 'PUT',
        body: JSON.stringify({
          maxMessagesPerMonth:    Number.isInteger(draft.maxMessagesPerMonth)    ? draft.maxMessagesPerMonth    : undefined,
          maxActiveConversations: Number.isInteger(draft.maxActiveConversations) ? draft.maxActiveConversations : undefined,
          maxCatalogSyncsPerDay:  Number.isInteger(draft.maxCatalogSyncsPerDay)  ? draft.maxCatalogSyncsPerDay  : undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setTiers((cur) => cur.map((t) => (t.tier === tier ? json.data : t)));
      setTierDraft((cur) => { const next = { ...cur }; delete next[tier]; return next; });
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingTier(null);
    }
  }, [tierDraft]);

  const [searchResults, setSearchResults] = useState([]);

  // Search merchants by email / store name / user id prefix. Results
  // dropdown lets the admin pick — no more pasting raw ids.
  const searchMerchants = useCallback(async (q) => {
    const query = String(q || '').trim();
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchState({ loading: true, error: '' });
    try {
      const res = await api(`/admin/whatsapp/merchants/search?q=${encodeURIComponent(query)}`);
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setSearchResults(json.data || []);
    } catch (err) {
      setSearchState({ loading: false, error: err.message });
      return;
    }
    setSearchState({ loading: false, error: '' });
  }, []);

  const loadMerchantLimits = useCallback(async (userId) => {
    setSearchState({ loading: true, error: '' });
    try {
      const res = await api(`/admin/whatsapp/limits/merchant/${encodeURIComponent(userId)}`);
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setOverrideForm(json.data);
      setSearchResults([]); // collapse dropdown once selected
    } catch (err) {
      setSearchState({ loading: false, error: err.message });
      return;
    }
    setSearchState({ loading: false, error: '' });
  }, []);

  // Backwards compat with saveOverride/clearOverride refreshing.
  const searchMerchant = useCallback(async () => {
    if (overrideForm?.merchant?.id) {
      await loadMerchantLimits(overrideForm.merchant.id);
    }
  }, [overrideForm, loadMerchantLimits]);

  // Debounce the free-text search so we don't hammer the endpoint on each keypress.
  useEffect(() => {
    const timer = setTimeout(() => searchMerchants(merchantSearch), 250);
    return () => clearTimeout(timer);
  }, [merchantSearch, searchMerchants]);

  const saveOverride = useCallback(async ({ messageLimit, conversationLimit, expiresAt, reason }) => {
    if (!overrideForm) return;
    setSearchState({ loading: true, error: '' });
    try {
      const res = await api(`/admin/whatsapp/limits/merchant/${overrideForm.merchant.id}/override`, {
        method: 'POST',
        body: JSON.stringify({
          messageLimit:      Number.isInteger(messageLimit)      ? messageLimit      : null,
          conversationLimit: Number.isInteger(conversationLimit) ? conversationLimit : null,
          expiresAt:         expiresAt || null,
          reason,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      // Reload merchant view after mutation.
      await searchMerchant();
    } catch (err) {
      setSearchState({ loading: false, error: err.message });
    }
  }, [overrideForm, searchMerchant]);

  const clearOverride = useCallback(async (reason) => {
    if (!overrideForm) return;
    setSearchState({ loading: true, error: '' });
    try {
      const res = await api(`/admin/whatsapp/limits/merchant/${overrideForm.merchant.id}/override`, {
        method: 'DELETE',
        body: JSON.stringify({ reason }),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      await searchMerchant();
    } catch (err) {
      setSearchState({ loading: false, error: err.message });
    }
  }, [overrideForm, searchMerchant]);

  return (
    <div className="wapc-tab-body">
      {error && <div className="wapc-error"><AlertTriangle size={14} /> {error}</div>}

      <div className="wapc-limits-section">
        <h3 className="wapc-limits-title">Per-tier defaults</h3>
        <p className="wapc-hint"><Info size={14} /> -1 = unlimited. Changes audit-logged.</p>
        <div className="wapc-table-wrap">
          <table className="wapc-table">
            <thead>
              <tr>
                <th>Tier</th>
                <th>Messages/mo</th>
                <th>Active convs</th>
                <th>Catalog syncs/day</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tiers.length === 0 && !loading && (
                <tr><td colSpan={5} className="wapc-empty">No tier limits seeded.</td></tr>
              )}
              {tiers.map((t) => {
                const draft = tierDraft[t.tier] || {};
                const val = (field) => draft[field] != null ? draft[field] : t[field];
                const dirty = draft.maxMessagesPerMonth != null || draft.maxActiveConversations != null || draft.maxCatalogSyncsPerDay != null;
                return (
                  <tr key={t.tier}>
                    <td><TierBadge tier={t.tier} /></td>
                    <td>
                      <input
                        type="number"
                        className="wapc-input-num"
                        value={val('maxMessagesPerMonth')}
                        onChange={(e) => setTierDraft((cur) => ({
                          ...cur,
                          [t.tier]: { ...cur[t.tier], maxMessagesPerMonth: e.target.value === '' ? null : parseInt(e.target.value, 10) },
                        }))}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="wapc-input-num"
                        value={val('maxActiveConversations')}
                        onChange={(e) => setTierDraft((cur) => ({
                          ...cur,
                          [t.tier]: { ...cur[t.tier], maxActiveConversations: e.target.value === '' ? null : parseInt(e.target.value, 10) },
                        }))}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="wapc-input-num"
                        value={val('maxCatalogSyncsPerDay')}
                        onChange={(e) => setTierDraft((cur) => ({
                          ...cur,
                          [t.tier]: { ...cur[t.tier], maxCatalogSyncsPerDay: e.target.value === '' ? null : parseInt(e.target.value, 10) },
                        }))}
                      />
                    </td>
                    <td>
                      {dirty && (
                        <button
                          type="button"
                          className="wapc-btn wapc-btn--primary"
                          onClick={() => saveTier(t.tier)}
                          disabled={savingTier === t.tier}
                        >
                          {savingTier === t.tier ? 'Saving…' : 'Save'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="wapc-limits-section">
        <h3 className="wapc-limits-title">Per-merchant overrides</h3>
        <p className="wapc-hint">
          <Info size={14} /> Search by email, store name, or user id. Override auto-reverts at expiry.
        </p>
        <div className="wapc-merchant-picker">
          <div className="wapc-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Type merchant email, business name, or user id…"
              value={merchantSearch}
              onChange={(e) => setMerchantSearch(e.target.value)}
              autoComplete="off"
            />
          </div>
          {searchResults.length > 0 && (
            <ul className="wapc-merchant-results">
              {searchResults.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    className="wapc-merchant-result-btn"
                    onClick={() => { setMerchantSearch(''); loadMerchantLimits(m.id); }}
                  >
                    <span className="wapc-merchant-result__main">
                      {m.storeName || m.email || m.id.slice(0, 8)}
                    </span>
                    <span className="wapc-merchant-result__meta">
                      {m.email ? m.email : ''}
                      {m.storeSlug ? ` • /${m.storeSlug}` : ''}
                    </span>
                    <TierBadge tier={m.tier} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {merchantSearch.length >= 2 && searchResults.length === 0 && !searchState.loading && (
            <p className="wapc-hint">No merchants match "{merchantSearch}".</p>
          )}
        </div>
        {searchState.error && <div className="wapc-error"><AlertTriangle size={14} /> {searchState.error}</div>}
        {overrideForm && (
          <OverrideEditor
            data={overrideForm}
            onSave={saveOverride}
            onClear={clearOverride}
          />
        )}
      </div>
    </div>
  );
}

function OverrideEditor({ data, onSave, onClear }) {
  const [msgLimit, setMsgLimit]     = useState(data.override.messageLimit ?? '');
  const [convLimit, setConvLimit]   = useState(data.override.conversationLimit ?? '');
  const [expiresAt, setExpiresAt]   = useState(data.override.expiresAt ? new Date(data.override.expiresAt).toISOString().slice(0, 16) : '');
  const [reason, setReason]         = useState('');
  const hasOverride = data.override.messageLimit != null || data.override.conversationLimit != null;

  useEffect(() => {
    setMsgLimit(data.override.messageLimit ?? '');
    setConvLimit(data.override.conversationLimit ?? '');
    setExpiresAt(data.override.expiresAt ? new Date(data.override.expiresAt).toISOString().slice(0, 16) : '');
    setReason('');
  }, [data]);

  const submit = () => {
    if (reason.trim().length < 3) return;
    onSave({
      messageLimit:      msgLimit === '' ? null : parseInt(msgLimit, 10),
      conversationLimit: convLimit === '' ? null : parseInt(convLimit, 10),
      expiresAt:         expiresAt || null,
      reason:            reason.trim(),
    });
  };

  return (
    <div className="wapc-override-editor">
      <div className="wapc-kv"><span>Merchant</span><strong>{data.merchant.storeName || data.merchant.email}</strong></div>
      <div className="wapc-kv"><span>Tier</span><strong><TierBadge tier={data.merchant.tier} /></strong></div>
      <div className="wapc-kv"><span>Effective msg cap</span><strong>{capLabel(data.effective.maxMessagesPerMonth)}</strong></div>
      <div className="wapc-kv"><span>Effective conv cap</span><strong>{capLabel(data.effective.maxActiveConversations)}</strong></div>

      <div className="wapc-override-grid">
        <label>
          <span>Message override (blank = tier default, -1 = unlimited)</span>
          <input type="number" className="wapc-input-num" value={msgLimit} onChange={(e) => setMsgLimit(e.target.value)} />
        </label>
        <label>
          <span>Conversation override</span>
          <input type="number" className="wapc-input-num" value={convLimit} onChange={(e) => setConvLimit(e.target.value)} />
        </label>
        <label>
          <span>Expires at (local, blank = never)</span>
          <input type="datetime-local" className="wapc-input-num" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
        </label>
        <label>
          <span>Reason (min 3 chars, required)</span>
          <input type="text" className="wapc-input-num" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Support ticket #… / temporary boost until upgrade" />
        </label>
      </div>

      <div className="wapc-actions">
        <button type="button" className="wapc-btn wapc-btn--primary" onClick={submit} disabled={reason.trim().length < 3}>
          Save override
        </button>
        {hasOverride && (
          <button type="button" className="wapc-btn wapc-btn--danger-ghost" onClick={() => reason.trim().length >= 3 && onClear(reason.trim())} disabled={reason.trim().length < 3}>
            Clear override
          </button>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  Defaults tab — platform-level new-merchant defaults + kill switch      */
/* ══════════════════════════════════════════════════════════════════════ */

function DefaultsTab() {
  const [data, setData]     = useState(null);
  const [draft, setDraft]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [killConfirm, setKillConfirm] = useState(null); // { turnOn, reason, phrase, submitting }

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api('/admin/whatsapp/defaults');
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setData(json.data);
      setDraft(json.data.defaults);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveDefaults = useCallback(async () => {
    setSaving(true);
    setError('');
    try {
      const res = await api('/admin/whatsapp/defaults', {
        method: 'PUT',
        body: JSON.stringify(draft),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setData(json.data);
      setDraft(json.data.defaults);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }, [draft]);

  const flipKillSwitch = useCallback(async (turnOn, reason) => {
    setKillConfirm((c) => c ? { ...c, submitting: true } : c);
    setError('');
    try {
      const res = await api('/admin/whatsapp/kill-switch', {
        method: 'POST',
        body: JSON.stringify({ on: turnOn, reason: reason || null }),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setData(json.data);
      setKillConfirm(null);
    } catch (err) {
      setError(err.message);
      setKillConfirm((c) => c ? { ...c, submitting: false } : c);
    }
  }, []);

  const dirty = data && draft && (
    draft.aiAssistantEnabled !== data.defaults.aiAssistantEnabled ||
    Number(draft.autoResumeMinutes) !== data.defaults.autoResumeMinutes ||
    draft.productAutoReplies !== data.defaults.productAutoReplies ||
    draft.orderStatusReplies !== data.defaults.orderStatusReplies
  );

  if (loading || !data || !draft) return <div className="wapc-detail-loading">Loading defaults…</div>;

  const killOn = data.killSwitch.on;

  return (
    <div className="wapc-tab-body">
      {error && <div className="wapc-error"><AlertTriangle size={14} /> {error}</div>}

      {/* ── Kill switch card ─────────────────────────────────── */}
      <div className={`wapc-kill-card ${killOn ? 'wapc-kill-card--on' : ''}`}>
        <div className="wapc-kill-card__head">
          <AlertTriangle size={22} />
          <div>
            <h3>Platform-wide AI kill switch</h3>
            <p>
              {killOn
                ? 'ACTIVE — every merchant\'s assistant is silenced. Inbound messages still deliver to inboxes; bots do not reply.'
                : 'When active, every merchant\'s assistant goes silent regardless of their own settings. Use for compliance emergencies or mass abuse response.'}
            </p>
          </div>
        </div>

        {killOn && (
          <div className="wapc-kill-card__status">
            <div className="wapc-kv"><span>Since</span><strong>{data.killSwitch.onAt ? new Date(data.killSwitch.onAt).toLocaleString() : '—'}</strong></div>
            <div className="wapc-kv"><span>Reason</span><strong>{data.killSwitch.reason || '—'}</strong></div>
            <div className="wapc-kv"><span>By</span><strong>{data.killSwitch.onBy || '—'}</strong></div>
          </div>
        )}

        <div className="wapc-actions">
          {!killOn && (
            <button
              type="button"
              className="wapc-btn wapc-btn--danger"
              onClick={() => setKillConfirm({ turnOn: true, reason: '', phrase: '', submitting: false })}
            >
              Silence all bots
            </button>
          )}
          {killOn && (
            <button
              type="button"
              className="wapc-btn wapc-btn--primary"
              onClick={() => setKillConfirm({ turnOn: false, reason: '', phrase: '', submitting: false })}
            >
              Resume all bots
            </button>
          )}
        </div>
      </div>

      {/* ── New-merchant defaults ────────────────────────────── */}
      <div className="wapc-limits-section">
        <h3 className="wapc-limits-title">New-merchant automation defaults</h3>
        <p className="wapc-hint">
          <Info size={14} /> Applied to merchants who haven't saved their own automation controls yet. Does NOT retroactively change existing merchants.
        </p>

        <div className="wapc-defaults-grid">
          <label className="wapc-defaults-row">
            <span>
              <strong>AI assistant enabled</strong>
              <em>Master switch new merchants inherit.</em>
            </span>
            <input
              type="checkbox"
              checked={!!draft.aiAssistantEnabled}
              onChange={(e) => setDraft({ ...draft, aiAssistantEnabled: e.target.checked })}
            />
          </label>
          <label className="wapc-defaults-row">
            <span>
              <strong>Product answers</strong>
              <em>Product / stock / price Q&amp;A auto-reply.</em>
            </span>
            <input
              type="checkbox"
              checked={!!draft.productAutoReplies}
              onChange={(e) => setDraft({ ...draft, productAutoReplies: e.target.checked })}
            />
          </label>
          <label className="wapc-defaults-row">
            <span>
              <strong>Order status answers</strong>
              <em>Payment / delivery status Q&amp;A auto-reply.</em>
            </span>
            <input
              type="checkbox"
              checked={!!draft.orderStatusReplies}
              onChange={(e) => setDraft({ ...draft, orderStatusReplies: e.target.checked })}
            />
          </label>
          <label className="wapc-defaults-row wapc-defaults-row--number">
            <span>
              <strong>Auto-resume window (minutes)</strong>
              <em>How long after a merchant reply before AI resumes. 1–120.</em>
            </span>
            <input
              type="number"
              min={1}
              max={120}
              className="wapc-input-num"
              value={draft.autoResumeMinutes}
              onChange={(e) => setDraft({ ...draft, autoResumeMinutes: parseInt(e.target.value, 10) || 1 })}
            />
          </label>
        </div>

        <div className="wapc-actions">
          <button
            type="button"
            className="wapc-btn wapc-btn--primary"
            onClick={saveDefaults}
            disabled={!dirty || saving}
          >
            {saving ? 'Saving…' : 'Save defaults'}
          </button>
          {dirty && (
            <button
              type="button"
              className="wapc-btn wapc-btn--ghost"
              onClick={() => setDraft(data.defaults)}
              disabled={saving}
            >
              Discard changes
            </button>
          )}
        </div>
      </div>

      {killConfirm && (
        <KillSwitchConfirm
          turnOn={killConfirm.turnOn}
          submitting={killConfirm.submitting}
          onCancel={() => setKillConfirm(null)}
          onConfirm={(reason) => flipKillSwitch(killConfirm.turnOn, reason)}
        />
      )}
    </div>
  );
}

/**
 * Confirm modal for the nuclear button. Turning ON requires the admin
 * to type the exact phrase — deliberate friction so a stray click on
 * the wrong tab can't silence the whole platform. Turning OFF is
 * lighter — just a reason.
 */
function KillSwitchConfirm({ turnOn, submitting, onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  const [phrase, setPhrase] = useState('');
  const requiredPhrase = 'SILENCE ALL BOTS';
  const canConfirm = turnOn
    ? phrase === requiredPhrase && reason.trim().length >= 3
    : reason.trim().length >= 3;

  return (
    <div className="wapc-modal-scrim" onClick={onCancel}>
      <div className="wapc-modal" onClick={(e) => e.stopPropagation()}>
        <h4>{turnOn ? 'Silence every merchant\'s bot?' : 'Resume every merchant\'s bot?'}</h4>
        <p className="wapc-modal__msg">
          {turnOn
            ? 'This affects EVERY merchant on the platform. Their assistants will stop replying until you flip this off. Merchant inboxes stay functional — only bot responses are gated.'
            : 'Bot responses resume across the platform. Each merchant\'s per-connection settings still apply as usual.'}
        </p>

        {turnOn && (
          <label className="wapc-modal__reason">
            <span>Type <code>{requiredPhrase}</code> to confirm</span>
            <input
              type="text"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder={requiredPhrase}
              autoComplete="off"
            />
          </label>
        )}
        <label className="wapc-modal__reason">
          <span>Reason (min 3 chars)</span>
          <textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={turnOn ? 'Compliance incident, mass abuse, etc.' : 'Investigation complete, resuming service.'}
          />
        </label>

        <div className="wapc-modal__actions">
          <button type="button" className="wapc-btn wapc-btn--ghost" onClick={onCancel} disabled={submitting}>Cancel</button>
          <button
            type="button"
            className={`wapc-btn ${turnOn ? 'wapc-btn--danger' : 'wapc-btn--primary'}`}
            onClick={() => onConfirm(reason.trim())}
            disabled={!canConfirm || submitting}
          >
            {submitting ? 'Working…' : (turnOn ? 'Silence all bots' : 'Resume all bots')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  Broadcast tab                                                           */
/* ══════════════════════════════════════════════════════════════════════ */

function BroadcastTab() {
  const [overview, setOverview] = useState(null);
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [allowlist, setAllowlist] = useState({ ids: [], merchants: [] });
  const [merchantQuery, setMerchantQuery] = useState('');
  const [merchantOptions, setMerchantOptions] = useState([]);
  const [suppressions, setSuppressions] = useState([]);
  const [suppressionPhone, setSuppressionPhone] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      const suppressionQs = suppressionPhone.trim()
        ? `?phone=${encodeURIComponent(suppressionPhone.trim())}&limit=10`
        : '?limit=10';
      const [overviewRes, campaignsRes, allowlistRes, suppressionsRes] = await Promise.all([
        api('/admin/whatsapp/broadcast/overview'),
        api(`/admin/whatsapp/broadcast/campaigns${qs}`),
        api('/admin/whatsapp/broadcast/allowlist'),
        api(`/admin/whatsapp/broadcast/suppressions${suppressionQs}`),
      ]);
      const [overviewJson, campaignsJson, allowlistJson, suppressionsJson] = await Promise.all([
        overviewRes.json(),
        campaignsRes.json(),
        allowlistRes.json(),
        suppressionsRes.json(),
      ]);
      if (!overviewRes.ok || !overviewJson?.success) {
        throw new Error(overviewJson?.error || `HTTP ${overviewRes.status}`);
      }
      if (!campaignsRes.ok || !campaignsJson?.success) {
        throw new Error(campaignsJson?.error || `HTTP ${campaignsRes.status}`);
      }
      if (!allowlistRes.ok || !allowlistJson?.success) {
        throw new Error(allowlistJson?.error || `HTTP ${allowlistRes.status}`);
      }
      if (!suppressionsRes.ok || !suppressionsJson?.success) {
        throw new Error(suppressionsJson?.error || `HTTP ${suppressionsRes.status}`);
      }
      setOverview(overviewJson.data?.overview || null);
      setSettings(overviewJson.data?.settings || null);
      setTemplates(overviewJson.data?.templates || []);
      setCampaigns(campaignsJson.data || []);
      setAllowlist(allowlistJson.data || { ids: [], merchants: [] });
      setSuppressions(suppressionsJson.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [status, suppressionPhone]);

  useEffect(() => { load(); }, [load]);

  const patchSetting = async (patch) => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const res = await api('/admin/whatsapp/broadcast/settings', {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setSettings(json.data);
      setNotice('Broadcast settings updated.');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const campaignAction = async (campaign, action) => {
    const reason = action === 'cancel'
      ? window.prompt('Reason for cancellation?') || ''
      : '';
    setError('');
    setNotice('');
    try {
      const res = await api(`/admin/whatsapp/broadcast/campaigns/${campaign.id}/${action}`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      const past = action === 'cancel' ? 'cancelled' : action === 'pause' ? 'paused' : action === 'retry' ? 'queued for retry' : 'resumed';
      setNotice(`Campaign ${past}.`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const workerAction = async (action) => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const endpoint = action === 'recover'
        ? '/admin/whatsapp/broadcast/worker/recover-stale'
        : '/admin/whatsapp/broadcast/worker/run';
      const body = action === 'recover'
        ? {
            staleMinutes: Number(settings?.worker?.staleSendingMinutes || 15),
            limit: Number(settings?.worker?.batchSize || 5),
          }
        : {
            limit: Number(settings?.worker?.batchSize || 5),
          };
      const res = await api(endpoint, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      const data = json.data || {};
      if (action === 'recover') {
        setNotice(`Recovered ${data.recovered || 0} stale campaign${data.recovered === 1 ? '' : 's'}.`);
      } else {
        const recovered = data.recovery?.recovered || 0;
        setNotice(`Worker processed ${data.processed || 0} campaign${data.processed === 1 ? '' : 's'}${recovered ? ` and recovered ${recovered} stale` : ''}.`);
      }
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const exportCampaign = async (campaign) => {
    if (!campaign?.id) return;
    setError('');
    setNotice('');
    try {
      const res = await api(`/admin/whatsapp/broadcast/campaigns/${campaign.id}/export`, {
        headers: { Accept: 'text/csv' },
      });
      if (!res.ok) {
        let message = `HTTP ${res.status}`;
        try {
          const json = await res.json();
          message = json?.error || message;
        } catch (_) {}
        throw new Error(message);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `broadcast-${campaign.id}-recipients.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setNotice('Broadcast export downloaded.');
    } catch (err) {
      setError(err.message);
    }
  };

  const updateTemplate = async (template, patch) => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const res = await api(`/admin/whatsapp/broadcast/templates/${template.id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setTemplates((rows) => rows.map((row) => (row.id === template.id ? json.data : row)));
      setNotice('Broadcast template updated.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveAllowlist = async (merchantIds) => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const res = await api('/admin/whatsapp/broadcast/allowlist', {
        method: 'PATCH',
        body: JSON.stringify({ merchantIds }),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setAllowlist(json.data || { ids: merchantIds, merchants: [] });
      setNotice('Broadcast allowlist updated.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addAllowlistMerchant = (merchant) => {
    if (!merchant?.id || allowlist.ids.includes(merchant.id)) return;
    saveAllowlist([...(allowlist.ids || []), merchant.id]);
    setMerchantQuery('');
    setMerchantOptions([]);
  };

  const removeAllowlistMerchant = (merchantId) => {
    saveAllowlist((allowlist.ids || []).filter((id) => id !== merchantId));
  };

  const removeSuppression = async (entry) => {
    if (!window.confirm(`Remove opt-out for ${entry.phone}?`)) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const res = await api(`/admin/whatsapp/broadcast/suppressions/${entry.id}`, { method: 'DELETE' });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setSuppressions((rows) => rows.filter((row) => row.id !== entry.id));
      setNotice('Suppression entry removed.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const query = merchantQuery.trim();
    if (query.length < 2) {
      setMerchantOptions([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await api(`/admin/whatsapp/broadcast/merchant-search?search=${encodeURIComponent(query)}`);
        const json = await res.json();
        if (!cancelled) setMerchantOptions(res.ok && json?.success ? json.data || [] : []);
      } catch {
        if (!cancelled) setMerchantOptions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [merchantQuery]);

  const openCampaignDetail = async (campaign) => {
    setDetailLoading(true);
    setSelectedCampaign({ ...campaign, recipients: [] });
    setError('');
    try {
      const res = await api(`/admin/whatsapp/broadcast/campaigns/${campaign.id}`);
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setSelectedCampaign(json.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const cards = [
    ['Campaigns', overview?.campaigns?.total || 0, `${overview?.campaigns?.today || 0} today`],
    ['Queued', overview?.campaigns?.queued || 0, `${overview?.campaigns?.sending || 0} sending`],
    ['Worker', overview?.worker?.enabled === false ? 'Off' : 'On', `${overview?.worker?.staleSending || 0} stale`],
    ['Completed', overview?.campaigns?.completed || 0, 'Finished'],
    ['Revenue', money(overview?.money?.finalCost, overview?.money?.currency), `${money(overview?.money?.refundAmount, overview?.money?.currency)} refunded`],
  ];
  const enabledTypes = Array.isArray(settings?.campaignTypes) && settings.campaignTypes.length
    ? settings.campaignTypes
    : ['STORE_LAUNCH'];
  const toggleCampaignType = (type, checked) => {
    const next = checked
      ? [...new Set([...enabledTypes, type])]
      : enabledTypes.filter((value) => value !== type);
    if (next.length === 0) return;
    patchSetting({ campaignTypes: next });
  };

  return (
    <div className="wapc-tab-body">
      {error && <div className="wapc-error"><Info size={14} /> {error}</div>}
      {notice && <div className="wapc-success"><Info size={14} /> {notice}</div>}

      <div className="wapc-broadcast-grid">
        {cards.map(([label, value, meta]) => (
          <div className="wapc-broadcast-stat" key={label}>
            <span>{label}</span>
            <strong>{loading ? '…' : value}</strong>
            <small>{meta}</small>
          </div>
        ))}
      </div>

      <section className="wapc-broadcast-settings">
        <div>
          <h3>Dark Launch Controls</h3>
          <p>
            Build and test the production broadcast engine without exposing the full composer publicly.
          </p>
        </div>

        <div className="wapc-broadcast-controls">
          <label className="wapc-switch-row">
            <input
              type="checkbox"
              checked={settings?.enabled === true}
              disabled={saving || !settings}
              onChange={(e) => patchSetting({ enabled: e.target.checked })}
            />
            <span>
              <strong>Broadcast API enabled</strong>
              <small>Allows allowlisted/dark-launch merchant requests.</small>
            </span>
          </label>

          <label className="wapc-switch-row">
            <input
              type="checkbox"
              checked={settings?.darkLaunch !== false}
              disabled={saving || !settings}
              onChange={(e) => patchSetting({ darkLaunch: e.target.checked })}
            />
            <span>
              <strong>Keep dark-launched</strong>
              <small>Do not surface the full merchant composer publicly.</small>
            </span>
          </label>

          <label className="wapc-switch-row">
            <input
              type="checkbox"
              checked={settings?.publicReleaseConfirmed === true}
              disabled={saving || !settings}
              onChange={(e) => patchSetting({ publicReleaseConfirmed: e.target.checked })}
            />
            <span>
              <strong>Public release approved</strong>
              <small>Required before dark launch can be turned off for every merchant.</small>
            </span>
          </label>

          <div className="wapc-setting-row">
            <label>Sender mode</label>
            <select
              className="wapc-select"
              value={settings?.senderMode || 'AUTO'}
              disabled={saving || !settings}
              onChange={(e) => patchSetting({ senderMode: e.target.value })}
            >
              <option value="AUTO">Auto</option>
              <option value="PLATFORM">Revstar AI platform number</option>
              <option value="MERCHANT">Merchant sender preferred</option>
            </select>
          </div>

          <div className="wapc-broadcast-inline">
            <label>
              <span>Unit cost</span>
              <input
                type="number"
                min="0"
                step="1"
                value={settings?.unitCost ?? ''}
                disabled={saving || !settings}
                onChange={(e) => setSettings((s) => ({ ...s, unitCost: e.target.value }))}
                onBlur={(e) => patchSetting({ unitCost: Number(e.target.value || 0) })}
              />
            </label>
            <label>
              <span>Per send</span>
              <input
                type="number"
                min="1"
                max="1000"
                value={settings?.perSend ?? ''}
                disabled={saving || !settings}
                onChange={(e) => setSettings((s) => ({ ...s, perSend: e.target.value }))}
                onBlur={(e) => patchSetting({ perSend: Number(e.target.value || 1) })}
              />
            </label>
            <label>
              <span>Monthly</span>
              <input
                type="number"
                min="-1"
                value={settings?.monthly ?? ''}
                disabled={saving || !settings}
                onChange={(e) => setSettings((s) => ({ ...s, monthly: e.target.value }))}
                onBlur={(e) => patchSetting({ monthly: Number(e.target.value || 0) })}
              />
            </label>
          </div>

          <label className="wapc-switch-row">
            <input
              type="checkbox"
              checked={settings?.worker?.enabled !== false}
              disabled={saving || !settings}
              onChange={(e) => patchSetting({ worker: { enabled: e.target.checked } })}
            />
            <span>
              <strong>Queue worker enabled</strong>
              <small>When off, campaigns can queue but the cron worker will not dispatch them.</small>
            </span>
          </label>

          <div className="wapc-broadcast-inline">
            <label>
              <span>Worker batch</span>
              <input
                type="number"
                min="1"
                max="25"
                value={settings?.worker?.batchSize ?? ''}
                disabled={saving || !settings}
                onChange={(e) => setSettings((s) => ({ ...s, worker: { ...(s?.worker || {}), batchSize: e.target.value } }))}
                onBlur={(e) => patchSetting({ worker: { batchSize: Number(e.target.value || 1) } })}
              />
            </label>
            <label>
              <span>Attempts</span>
              <input
                type="number"
                min="1"
                max="10"
                value={settings?.worker?.maxAttempts ?? ''}
                disabled={saving || !settings}
                onChange={(e) => setSettings((s) => ({ ...s, worker: { ...(s?.worker || {}), maxAttempts: e.target.value } }))}
                onBlur={(e) => patchSetting({ worker: { maxAttempts: Number(e.target.value || 1) } })}
              />
            </label>
            <label>
              <span>Retry min</span>
              <input
                type="number"
                min="1"
                max="1440"
                value={settings?.worker?.retryDelayMinutes ?? ''}
                disabled={saving || !settings}
                onChange={(e) => setSettings((s) => ({ ...s, worker: { ...(s?.worker || {}), retryDelayMinutes: e.target.value } }))}
                onBlur={(e) => patchSetting({ worker: { retryDelayMinutes: Number(e.target.value || 1) } })}
              />
            </label>
            <label>
              <span>Backoff</span>
              <input
                type="number"
                min="1"
                max="10"
                step="0.25"
                value={settings?.worker?.backoffMultiplier ?? ''}
                disabled={saving || !settings}
                onChange={(e) => setSettings((s) => ({ ...s, worker: { ...(s?.worker || {}), backoffMultiplier: e.target.value } }))}
                onBlur={(e) => patchSetting({ worker: { backoffMultiplier: Number(e.target.value || 1) } })}
              />
            </label>
            <label>
              <span>Stale min</span>
              <input
                type="number"
                min="5"
                max="1440"
                value={settings?.worker?.staleSendingMinutes ?? ''}
                disabled={saving || !settings}
                onChange={(e) => setSettings((s) => ({ ...s, worker: { ...(s?.worker || {}), staleSendingMinutes: e.target.value } }))}
                onBlur={(e) => patchSetting({ worker: { staleSendingMinutes: Number(e.target.value || 5) } })}
              />
            </label>
          </div>

          <div className="wapc-broadcast-inline">
            <label>
              <span>Deferred</span>
              <input type="text" value={overview?.worker?.queuedDeferred ?? 0} disabled readOnly />
            </label>
            <label>
              <span>Dead letters</span>
              <input type="text" value={overview?.worker?.deadLetterRecipients ?? 0} disabled readOnly />
            </label>
            <label>
              <span>Next retry</span>
              <input type="text" value={overview?.worker?.nextAttemptAt ? relTime(overview.worker.nextAttemptAt) : '-'} disabled readOnly />
            </label>
          </div>

          <div className="wapc-row-actions">
            <button type="button" className="wapc-btn wapc-btn--primary" disabled={saving || !settings} onClick={() => workerAction('run')}>
              Run Queue Once
            </button>
            <button type="button" className="wapc-btn wapc-btn--ghost" disabled={saving || !settings} onClick={() => workerAction('recover')}>
              Recover Stale Now
            </button>
          </div>

          <div className="wapc-type-control">
            <span>Allowed campaign types</span>
            <div>
              {BROADCAST_TYPES.map((type) => (
                <label key={type.value}>
                  <input
                    type="checkbox"
                    checked={enabledTypes.includes(type.value)}
                    disabled={saving || !settings || (enabledTypes.length === 1 && enabledTypes.includes(type.value))}
                    onChange={(e) => toggleCampaignType(type.value, e.target.checked)}
                  />
                  <span>{type.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="wapc-template-strip">
        <header>
          <h3>Template Catalog</h3>
          <p>Only active templates can be used by merchant broadcast campaigns.</p>
        </header>
        <div className="wapc-template-grid">
          {templates.map((template) => (
            <article key={template.id || template.key} className={`wapc-template-card is-${String(template.status || '').toLowerCase()}`}>
              <div>
                <strong>{template.name}</strong>
                <span>{template.type}</span>
              </div>
              <label>
                <span>Meta template</span>
                <input
                  value={template.metaTemplateName || ''}
                  disabled={saving}
                  onChange={(e) => setTemplates((rows) => rows.map((row) => (
                    row.id === template.id ? { ...row, metaTemplateName: e.target.value } : row
                  )))}
                  onBlur={(e) => updateTemplate(template, { metaTemplateName: e.target.value })}
                />
              </label>
              <div className="wapc-template-card__row">
                <label>
                  <span>Language</span>
                  <input
                    value={template.language || 'en'}
                    disabled={saving}
                    onChange={(e) => setTemplates((rows) => rows.map((row) => (
                      row.id === template.id ? { ...row, language: e.target.value } : row
                    )))}
                    onBlur={(e) => updateTemplate(template, { language: e.target.value })}
                  />
                </label>
                <label>
                  <span>Status</span>
                  <select
                    value={template.status || 'DRAFT'}
                    disabled={saving}
                    onChange={(e) => updateTemplate(template, { status: e.target.value })}
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="DRAFT">Draft</option>
                    <option value="DISABLED">Disabled</option>
                  </select>
                </label>
              </div>
            </article>
          ))}
        </div>
      </section>

      <div className="wapc-broadcast-admin-grid">
        <section className="wapc-admin-box">
          <header>
            <h3>Dark Launch Allowlist</h3>
            <p>{allowlist.ids?.length || 0} merchant{allowlist.ids?.length === 1 ? '' : 's'} can see the broadcast engine while dark-launched.</p>
          </header>
          <div className="wapc-allowlist-search">
            <Search size={15} />
            <input
              value={merchantQuery}
              onChange={(e) => setMerchantQuery(e.target.value)}
              placeholder="Search merchant email, code, or store"
            />
          </div>
          {merchantOptions.length > 0 && (
            <div className="wapc-merchant-options">
              {merchantOptions.map((merchant) => (
                <button key={merchant.id} type="button" onClick={() => addAllowlistMerchant(merchant)} disabled={allowlist.ids?.includes(merchant.id)}>
                  <strong>{merchant.storeName || merchant.email}</strong>
                  <span>{merchant.email} · {merchant.merchantTier}</span>
                </button>
              ))}
            </div>
          )}
          <div className="wapc-chip-list">
            {(allowlist.merchants || []).map((merchant) => (
              <span className="wapc-chip" key={merchant.id}>
                {merchant.storeName || merchant.email}
                <button type="button" onClick={() => removeAllowlistMerchant(merchant.id)} disabled={saving} aria-label={`Remove ${merchant.email || merchant.id}`}>
                  <X size={12} />
                </button>
              </span>
            ))}
            {(allowlist.merchants || []).length === 0 && <span className="wapc-muted">No dark-launch merchants yet.</span>}
          </div>
        </section>

        <section className="wapc-admin-box">
          <header>
            <h3>Opt-out Suppressions</h3>
            <p>Contacts here are skipped before wallet charging and sending.</p>
          </header>
          <div className="wapc-allowlist-search">
            <Search size={15} />
            <input
              value={suppressionPhone}
              onChange={(e) => setSuppressionPhone(e.target.value)}
              placeholder="Filter phone number"
            />
            <button type="button" className="wapc-btn wapc-btn--ghost" onClick={load} disabled={loading}>Search</button>
          </div>
          <div className="wapc-suppression-list">
            {suppressions.map((entry) => (
              <div key={entry.id} className="wapc-suppression-row">
                <div>
                  <strong>{entry.phone}</strong>
                  <span>{entry.scope} · {entry.reason} · {entry.merchant?.email || 'platform'}</span>
                </div>
                <button type="button" className="wapc-btn wapc-btn--danger-ghost" onClick={() => removeSuppression(entry)} disabled={saving}>
                  Remove
                </button>
              </div>
            ))}
            {suppressions.length === 0 && <div className="wapc-empty">No suppression entries found.</div>}
          </div>
        </section>
      </div>

      <div className="wapc-filters">
        <select className="wapc-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All campaign statuses</option>
          <option value="READY">Ready</option>
          <option value="QUEUED">Queued</option>
          <option value="PAUSED">Paused</option>
          <option value="SENDING">Sending</option>
          <option value="COMPLETED">Completed</option>
          <option value="PARTIAL">Partial</option>
          <option value="FAILED">Failed</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        <button type="button" className="wapc-btn" onClick={load} disabled={loading}>
          <RefreshCw size={14} /> {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <div className="wapc-table-wrap">
        <table className="wapc-table">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Merchant</th>
              <th>Status</th>
              <th>Sender</th>
              <th>Contacts</th>
              <th>Sent</th>
              <th>Cost</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {campaigns.length === 0 && !loading && (
              <tr><td colSpan={9} className="wapc-empty">No broadcast campaigns yet.</td></tr>
            )}
            {campaigns.map((campaign) => (
              <tr key={campaign.id}>
                <td>
                  <div className="wapc-merchant">
                    <strong>{campaign.name}</strong>
                    <span className="wapc-merchant__meta">{campaign.type}</span>
                  </div>
                </td>
                <td>
                  <div className="wapc-merchant">
                    <strong>{campaign.store?.name || campaign.merchant?.email || '—'}</strong>
                    <span className="wapc-merchant__meta">
                      {campaign.store?.slug ? `/${campaign.store.slug}` : campaign.merchant?.merchantTier || ''}
                    </span>
                  </div>
                </td>
                <td><StatusDot status={campaign.status} /></td>
                <td>
                  <span className="wapc-catalog wapc-catalog--ready">{campaign.senderType}</span>
                  <div className="wapc-mono">{campaign.senderPhoneNumberId || '—'}</div>
                </td>
                <td className="wapc-num">{campaign.contactCount}</td>
                <td className="wapc-num">{campaign.sentCount}</td>
                <td className="wapc-num">{money(campaign.finalCost || campaign.estimatedCost, campaign.currency)}</td>
                <td>{relTime(campaign.createdAt)}</td>
                <td>
                  <div className="wapc-row-actions">
                    <button type="button" className="wapc-btn wapc-btn--ghost" onClick={() => openCampaignDetail(campaign)}>
                      <Eye size={14} /> View
                    </button>
                    <button type="button" className="wapc-btn wapc-btn--ghost" onClick={() => exportCampaign(campaign)}>
                      Export
                    </button>
                    {['READY', 'QUEUED'].includes(campaign.status) && (
                      <button type="button" className="wapc-btn wapc-btn--ghost" onClick={() => campaignAction(campaign, 'pause')}>Pause</button>
                    )}
                    {campaign.status === 'PAUSED' && (
                      <button type="button" className="wapc-btn wapc-btn--ghost" onClick={() => campaignAction(campaign, 'resume')}>Resume</button>
                    )}
                    {['READY', 'QUEUED', 'PAUSED', 'FAILED'].includes(campaign.status) && (
                      <button type="button" className="wapc-btn wapc-btn--danger-ghost" onClick={() => campaignAction(campaign, 'cancel')}>Cancel</button>
                    )}
                    {['FAILED', 'PARTIAL'].includes(campaign.status) && (
                      <button type="button" className="wapc-btn wapc-btn--ghost" onClick={() => campaignAction(campaign, 'retry')}>Retry</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedCampaign && (
        <div className="wapc-drawer-backdrop" role="dialog" aria-modal="true" aria-label="Broadcast campaign detail" onClick={() => setSelectedCampaign(null)}>
          <aside className="wapc-broadcast-drawer" onClick={(event) => event.stopPropagation()}>
            <header>
              <div>
                <span className="wapc-kicker">Broadcast Detail</span>
                <h3>{selectedCampaign.name}</h3>
                <p>{selectedCampaign.merchant?.email || selectedCampaign.merchantId}</p>
              </div>
              <div className="wapc-row-actions">
                <button type="button" className="wapc-btn wapc-btn--ghost" onClick={() => exportCampaign(selectedCampaign)}>
                  Export
                </button>
                <button type="button" className="wapc-icon-btn" onClick={() => setSelectedCampaign(null)} aria-label="Close campaign detail">
                  <X size={18} />
                </button>
              </div>
            </header>

            <div className="wapc-drawer-stats">
              <div><span>Status</span><strong>{selectedCampaign.status}</strong></div>
              <div><span>Sender</span><strong>{selectedCampaign.senderType}</strong></div>
              <div><span>Contacts</span><strong>{selectedCampaign.contactCount}</strong></div>
              <div><span>Final cost</span><strong>{money(selectedCampaign.finalCost, selectedCampaign.currency)}</strong></div>
            </div>

            <dl className="wapc-drawer-meta">
              <div><dt>Debit ref</dt><dd>{selectedCampaign.walletDebitTransactionRef || '-'}</dd></div>
              <div><dt>Refund/release</dt><dd>{money(selectedCampaign.refundAmount, selectedCampaign.currency)}</dd></div>
              <div><dt>Phone number ID</dt><dd>{selectedCampaign.senderPhoneNumberId || '-'}</dd></div>
              <div><dt>Created</dt><dd>{relTime(selectedCampaign.createdAt)}</dd></div>
            </dl>

            <section className="wapc-drawer-recipients">
              <h4>Recipients</h4>
              {detailLoading ? (
                <div className="wapc-empty">Loading recipients...</div>
              ) : (
                <div className="wapc-recipient-list">
                  {(selectedCampaign.recipients || []).map((recipient) => (
                    <div className="wapc-recipient-row" key={recipient.id}>
                      <div>
                        <strong>{recipient.contactName || recipient.phone}</strong>
                        <span>{recipient.phone}</span>
                      </div>
                      <div>
                        <StatusDot status={recipient.status} />
                        {recipient.errorMessage && <small>{recipient.errorMessage}</small>}
                      </div>
                    </div>
                  ))}
                  {(selectedCampaign.recipients || []).length === 0 && (
                    <div className="wapc-empty">No recipients found.</div>
                  )}
                </div>
              )}
            </section>
          </aside>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  Shell                                                                   */
/* ══════════════════════════════════════════════════════════════════════ */

const WhatsAppControlCenter = () => {
  const [activeTab, setActiveTab] = useState('connections');

  return (
    <div className="wapc-page">
      <header className="wapc-page__head">
        <div>
          <h1 className="wapc-page__title">WhatsApp Control Center</h1>
          <p className="wapc-page__subtitle">
            Manage merchant WABAs, quotas, broadcasts, and platform-level automation from one place.
          </p>
        </div>
      </header>

      <nav className="wapc-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={activeTab === t.key}
            className={`wapc-tab ${activeTab === t.key ? 'wapc-tab--active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
            {t.stub && <span className="wapc-tab__stub">soon</span>}
          </button>
        ))}
      </nav>

      <section className="wapc-panel">
        {activeTab === 'connections' && <ConnectionsTab />}
        {activeTab === 'usage'    && <UsageTab />}
        {activeTab === 'limits'   && <LimitsTab />}
        {activeTab === 'defaults' && <DefaultsTab />}
        {activeTab === 'broadcast' && <BroadcastTab />}
      </section>
    </div>
  );
};

export default WhatsAppControlCenter;
